/**
 * Permissions utility functions for section-based access control
 * Supports both legacy role-based permissions and new granular section-based permissions
 */

// Available sections
export const SECTIONS = {
  JOURNEYS: 'journeys',
  CUSTOMERS: 'customers',
  PERSONAS: 'personas',
  INSIGHTS: 'insights',
};

/**
 * Check if user is an admin for the given organization
 * @param {Object} user - User object from Redux store
 * @param {string} orgId - Organization ID
 * @returns {boolean} - True if user is admin
 */
export function isAdmin(user, orgId) {
  if (!user || !orgId) return false;
  
  // User owns the organization
  if (orgId === user.uid) return false; // Owner is not technically an "admin" but has full access
  
  // Check if user has admin role
  if (user.linked && user.linked[orgId]) {
    return user.linked[orgId].role === 'admin';
  }
  
  return false;
}

/**
 * Get the permission level for a specific section
 * @param {Object} user - User object from Redux store
 * @param {string} orgId - Organization ID
 * @param {string} section - Section name (journeys, customers, personas)
 * @returns {string|null} - Permission level: "write", "view", or null (no access)
 */
export function getSectionPermission(user, orgId, section) {
  if (!user || !orgId || !section) return null;
  
  // User owns the organization - full access
  if (orgId === user.uid) return 'write';
  
  const linkedData = user.linked && user.linked[orgId] ? user.linked[orgId] : null;
  if (!linkedData) return null;
  
  // Admin users get write access to all sections (admin always has access regardless of permissions object)
  if (linkedData.role === 'admin') {
    return 'write';
  }
  
  // Check new permissions structure - if permissions object exists, it takes full precedence
  // If permissions object exists, any section not explicitly listed defaults to no access (null)
  if (linkedData.permissions && typeof linkedData.permissions === 'object') {
    // If section key exists in permissions, return its value (could be "write", "view", or null)
    if (linkedData.permissions.hasOwnProperty(section)) {
      return linkedData.permissions[section];
    }
    // If permissions object exists but section key doesn't, default to no access
    return null;
  }
  
  // Fall back to legacy role-based permissions only if permissions object doesn't exist at all
  if (linkedData.role === 'write') {
    return 'write';
  } else if (linkedData.role === 'read') {
    return 'view';
  }
  
  return null;
}

/**
 * Check if user can view a specific section
 * @param {Object} user - User object from Redux store
 * @param {string} orgId - Organization ID
 * @param {string} section - Section name
 * @returns {boolean} - True if user can view the section
 */
export function canViewSection(user, orgId, section) {
  const permission = getSectionPermission(user, orgId, section);
  return permission === 'write' || permission === 'view';
}

/**
 * Check if user can edit a specific section
 * @param {Object} user - User object from Redux store
 * @param {string} orgId - Organization ID
 * @param {string} section - Section name
 * @returns {boolean} - True if user can edit the section
 */
export function canEditSection(user, orgId, section) {
  const permission = getSectionPermission(user, orgId, section);
  return permission === 'write';
}

/**
 * Get all section permissions as an object
 * @param {Object} user - User object from Redux store
 * @param {string} orgId - Organization ID
 * @returns {Object} - Object with section permissions: { journeys: "write", customers: "view", personas: null }
 */
export function getAllSectionPermissions(user, orgId) {
  return {
    [SECTIONS.JOURNEYS]: getSectionPermission(user, orgId, SECTIONS.JOURNEYS),
    [SECTIONS.CUSTOMERS]: getSectionPermission(user, orgId, SECTIONS.CUSTOMERS),
    [SECTIONS.PERSONAS]: getSectionPermission(user, orgId, SECTIONS.PERSONAS),
    [SECTIONS.INSIGHTS]: getSectionPermission(user, orgId, SECTIONS.INSIGHTS),
  };
}

/**
 * Check if user can manage access (admin functionality)
 * @param {Object} user - User object from Redux store
 * @param {string} orgId - Organization ID
 * @returns {boolean} - True if user can manage access
 */
export function canManageAccess(user, orgId) {
  return isAdmin(user, orgId);
}


